import { API_ROUTES, CONFIG, isDevelopment } from '../../constants/app';
import { Typography, TypographyHeading } from '../../types/typography';
import axios from '../axios';
import {
  GuideProperties,
  NavigationBar,
  PortalInformation,
  Sidebar,
  ThemesetDetails,
  Themeset,
  PageHeader,
  Sections,
  ThemesetCard,
  ThemesetButton,
  ThemesetSpacer,
  ThemesetCollection,
  ThemesetColorPalette,
  ThemesetMedia,
} from '../../types';
import { Color } from '../../types/color';
import { Font } from '../../constants/themesets';
import { FontFamily } from '@bynder/fonts-js-sdk';

export async function fetchThemesetsApi(): Promise<Themeset[]> {
  return (await axios())
    .get(CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESETS)
    .then(res => res.data)
    .catch(e => {
      throw new Error(e);
    });
}

export async function duplicateThemeset(themesetId: string): Promise<Themeset> {
  return (await axios()).post(
    CONTENT_CUSTOMIZATION_ENDPOINTS.DUPLICATE_THEMESET(themesetId),
  );
}

export async function editTypography(
  themesetId: string,
  typography: {
    id: string;
    heading: TypographyHeading;
    label: string;
    fontFamily: string;
    fontStyle: string;
    fontSize: number;
    lineHeight: string | null;
    letterSpacing: string | null;
    fontColorId: string | null;
  },
): Promise<Typography> {
  return (await axios()).patch(
    CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_TYPOGRAPHY(themesetId),
    typography,
  );
}

export async function editNavBarApi(
  themesetId: string,
  navBarOptions: NavigationBar,
): Promise<NavigationBar> {
  return (await axios()).patch(
    CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_NAVBAR(themesetId),
    {
      backgroundColorId: navBarOptions.backgroundColor?.id || null,
      borderColorId: navBarOptions.borderColor?.id || null,
      textColorId: navBarOptions.textColor?.id || null,
      fontFamily: navBarOptions.fontFamily,
      fontStyle: navBarOptions.fontStyle,
      fontSize: navBarOptions.fontSize,
    },
  );
}

export async function editPageHeaderApi(
  themesetId: string,
  pageHeaderOptions: PageHeader,
): Promise<PageHeader> {
  const { backgroundColor, textColor } = pageHeaderOptions;
  const request = {
    backgroundColorId: backgroundColor?.id ?? null,
    textColorId: textColor?.id ?? null,
  };
  return (await axios()).patch(
    CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_PAGE_HEADER(themesetId),
    request,
  );
}
export async function editPageSectionApi(
  themesetId: string,
  pageSection: Sections,
): Promise<Sections> {
  const { backgroundColor } = pageSection;
  const request = {
    backgroundColorId: backgroundColor?.id ?? null,
  };
  return (await axios()).patch(
    CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_PAGE_SECTION(themesetId),
    request,
  );
}
export async function editThemesetCardApi(
  themesetId: string,
  themesetCard: ThemesetCard,
): Promise<ThemesetCard> {
  const { titleColor, descriptionColor, borderColor } = themesetCard;
  const request = {
    borderRadius: themesetCard.borderRadius ?? null,
    title: {
      fontFamily: themesetCard.title.fontFamily ?? null,
      fontStyle: themesetCard.title.fontStyle ?? null,
      fontSize: themesetCard.title.fontSize ?? null,
    },
    description: {
      fontFamily: themesetCard.description.fontFamily ?? null,
      fontStyle: themesetCard.description.fontStyle ?? null,
      fontSize: themesetCard.description.fontSize ?? null,
    },
    titleColorId: titleColor?.id ?? null,
    descriptionColorId: descriptionColor?.id ?? null,
    borderColorId: borderColor?.id ?? null,
  };

  return (await axios()).patch(
    CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_CARD_WIDGET(themesetId),
    request,
  );
}

export async function editThemesetSpacerApi(
  themesetId: string,
  themesetSpacer: ThemesetSpacer,
): Promise<ThemesetCard> {
  const { color } = themesetSpacer;
  const request = {
    colorId: color?.id ?? null,
  };

  return (await axios()).patch(
    CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_SPACER_WIDGET(themesetId),
    request,
  );
}

export async function editThemesetCollectionApi(
  themesetId: string,
  themesetCollection: ThemesetCollection,
): Promise<ThemesetCard> {
  const { collectionTitle, assetTitle, count, borderRadius } =
    themesetCollection;
  const request = {
    borderRadius: borderRadius ?? null,
    collectionTitle: {
      fontFamily: collectionTitle.fontFamily ?? null,
      fontStyle: collectionTitle.fontStyle ?? null,
      fontSize: collectionTitle.fontSize ?? null,
      textColorId: collectionTitle.textColor?.id ?? null,
    },
    assetTitle: {
      fontFamily: assetTitle.fontFamily ?? null,
      fontStyle: assetTitle.fontStyle ?? null,
      fontSize: assetTitle.fontSize ?? null,
      textColorId: assetTitle.textColor?.id ?? null,
    },
    count: {
      fontFamily: count.fontFamily ?? null,
      fontStyle: count.fontStyle ?? null,
      fontSize: count.fontSize ?? null,
      textColorId: count.textColor?.id ?? null,
    },
  };

  return (await axios()).patch(
    CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_COLLECTION_WIDGET(themesetId),
    request,
  );
}

export async function editThemesetColorPaletteApi(
  themesetId: string,
  themesetColorPalette: ThemesetColorPalette,
): Promise<ThemesetCard> {
  const { name, mode, value, borderRadius } = themesetColorPalette;
  const request = {
    borderRadius: borderRadius ?? null,
    name: {
      fontFamily: name.fontFamily ?? null,
      fontStyle: name.fontStyle ?? null,
      fontSize: name.fontSize ?? null,
      textColorId: name.textColor?.id ?? null,
    },
    mode: {
      fontFamily: mode.fontFamily ?? null,
      fontStyle: mode.fontStyle ?? null,
      fontSize: mode.fontSize ?? null,
      textColorId: mode.textColor?.id ?? null,
    },
    value: {
      fontFamily: value.fontFamily ?? null,
      fontStyle: value.fontStyle ?? null,
      fontSize: value.fontSize ?? null,
      textColorId: value.textColor?.id ?? null,
    },
  };

  return (await axios()).patch(
    CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_COLOR_PALETTE_WIDGET(themesetId),
    request,
  );
}

export async function editThemesetMediaApi(
  themesetId: string,
  options: ThemesetMedia,
) {
  const request = {
    borderRadius: options.borderRadius,
  };
  return (await axios()).patch(
    CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_MEDIA_WIDGET(themesetId),
    request,
  );
}

export async function editThemesetButtonApi(
  themesetId: string,
  themesetButton: ThemesetButton,
): Promise<ThemesetButton> {
  const request = {
    borderRadius: themesetButton.borderRadius,
    fontFamily: themesetButton.fontFamily,
    fontStyle: themesetButton.fontStyle,
    fontSize: themesetButton.fontSize,
    padding: {
      horizontalPadding: themesetButton.padding.horizontalPadding,
      verticalPadding: themesetButton.padding.verticalPadding,
    },
    colors: {
      hover: {
        textColorId: themesetButton.colors.hover.textColor?.id ?? null,
        backgroundColorId:
          themesetButton.colors.hover.backgroundColor?.id ?? null,
        borderColorId: themesetButton.colors.hover.borderColor?.id ?? null,
      },
      default: {
        textColorId: themesetButton.colors.default.textColor?.id ?? null,
        backgroundColorId:
          themesetButton.colors.default.backgroundColor?.id ?? null,
        borderColorId: themesetButton.colors.default.borderColor?.id ?? null,
      },
    },
  };

  return (await axios()).patch(
    CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_BUTTON_WIDGET(themesetId),
    request,
  );
}

export async function editSidebarApi(
  themesetId: string,
  sidebarOptions: Sidebar,
): Promise<Sidebar> {
  const { chapter, page, tableOfContents, backgroundColor, borderColor } =
    sidebarOptions;
  const requestObject = {
    backgroundColorId: backgroundColor?.id ?? null,
    borderColorId: borderColor?.id ?? null,
    chapter: {
      fontFamily: chapter.fontFamily,
      fontStyle: chapter.fontStyle,
      fontSize: chapter.fontSize,
      colorIds: {
        text: {
          normal: chapter.colors.text.normal?.id ?? null,
          hover: chapter.colors.text.hover?.id ?? null,
        },
        background: {
          hover: chapter.colors.background.hover?.id ?? null,
        },
      },
    },
    page: {
      fontFamily: page.fontFamily,
      fontStyle: page.fontStyle,
      fontSize: page.fontSize,
      colorIds: {
        text: {
          normal: page.colors.text.normal?.id ?? null,
          hover: page.colors.text.hover?.id ?? null,
          active: page.colors.text.active?.id ?? null,
        },
        background: {
          hover: page.colors.background.hover?.id ?? null,
          active: page.colors.background.active?.id ?? null,
        },
      },
    },
    tableOfContents: {
      fontFamily: tableOfContents.fontFamily,
      fontStyle: tableOfContents.fontStyle,
      fontSize: tableOfContents.fontSize,
      colorIds: {
        text: {
          normal: tableOfContents.colors.text.normal?.id ?? null,
          hover: tableOfContents.colors.text.hover?.id ?? null,
          active: tableOfContents.colors.text.active?.id ?? null,
        },
        background: {
          hover: tableOfContents.colors.background.hover?.id ?? null,
          active: tableOfContents.colors.background.active?.id ?? null,
        },
        accent: {
          normal: tableOfContents.colors.accent.normal?.id ?? null,
          hover: tableOfContents.colors.accent.hover?.id ?? null,
          active: tableOfContents.colors.accent.active?.id ?? null,
        },
      },
    },
  };

  return (await axios()).patch(
    CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_SIDEBAR(themesetId),
    requestObject,
  );
}

export async function assignThemesetToGuide(
  themesetId: string,
  guideId: string,
): Promise<Typography> {
  return (await axios()).patch(
    CONTENT_CUSTOMIZATION_ENDPOINTS.ASSIGN_THEMESET_TO_GUIDE(
      themesetId,
      guideId,
    ),
  );
}

export async function fetchAssignedThemeset(
  guideId: string,
): Promise<Themeset> {
  return (await axios())
    .get(CONTENT_CUSTOMIZATION_ENDPOINTS.ASSIGNED_THEMESET(guideId))
    .then(res => res.data)
    .catch(e => {
      throw new Error(e);
    });
}
interface ThemesetAssignationType {
  [key: string]: string[];
}
export async function fetchThemesetAssignationApi(): Promise<ThemesetAssignationType> {
  return (await axios())
    .get(CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_ASSIGNATION())
    .then(res => res.data)
    .catch(e => {
      throw new Error(e);
    });
}
export async function fetchJSONThemeApi() {
  return (await axios(false))
    .get(CONTENT_CUSTOMIZATION_ENDPOINTS.THEMES)
    .then(res => res.data)
    .catch(e => {
      throw new Error(e);
    });
}

export const fetchFontsApi = async (): Promise<Font[]> => {
  return (await axios(false))
    .get(CONTENT_CUSTOMIZATION_ENDPOINTS.FONTS_NEW.PUBLIC)
    .then(res => res.data);
};
export const fetchFontFacesApi = async (): Promise<Font[]> => {
  return (await axios(false))
    .get(CONTENT_CUSTOMIZATION_ENDPOINTS.FONTFACES)
    .then(res => res.data);
};

export const fetchGlobalFonts = async (): Promise<FontFamily[]> => {
  return (await axios(true))
    .get(CONTENT_CUSTOMIZATION_ENDPOINTS.GLOBAL_FONTS)
    .then(res => res.data.fonts);
};

export async function createThemesetColor(
  themesetId: string,
  colorSetId: string,
  color: Color,
): Promise<Typography> {
  return (await axios()).put(
    CONTENT_CUSTOMIZATION_ENDPOINTS.CREATE_THEMESET_COLOR(
      themesetId,
      colorSetId,
    ),
    {
      id: color.id,
      hexCode: color.hexCode,
      label: color.label,
    },
  );
}

export async function editThemesetColor(
  themesetId: string,
  colorSetId: string,
  color: Color,
): Promise<Typography> {
  return (await axios()).patch(
    CONTENT_CUSTOMIZATION_ENDPOINTS.EDIT_THEMESET_COLOR(
      themesetId,
      colorSetId,
      color.id,
    ),
    {
      id: color.id,
      hexCode: color.hexCode,
      label: color.label,
    },
  );
}

export async function deleteThemesetColor(
  themesetId: string,
  colorSetId: string,
  colorId: string,
): Promise<Typography> {
  return (await axios()).delete(
    CONTENT_CUSTOMIZATION_ENDPOINTS.DELETE_THEMESET_COLOR(
      themesetId,
      colorSetId,
      colorId,
    ),
  );
}

export async function editThemesetDetails(
  themesetId: string,
  themesetDetails: ThemesetDetails,
) {
  return (await axios()).patch(
    CONTENT_CUSTOMIZATION_ENDPOINTS.EDIT_THEMESET_DETAILS(themesetId),
    themesetDetails,
  );
}

export async function deleteThemeset(themesetId: string) {
  return (await axios()).delete(
    CONTENT_CUSTOMIZATION_ENDPOINTS.DELETE_THEMESET(themesetId),
  );
}

export async function createThemesetApi(
  themesetId: string,
  themesetDetails: ThemesetDetails,
) {
  return (await axios()).put(CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESETS, {
    id: themesetId,
    ...themesetDetails,
  });
}

export async function editGuidePropertiesApi(
  themesetId: string,
  guidePropertiesOptions: GuideProperties,
): Promise<GuideProperties> {
  const {
    logoId,
    logoUrl,
    landingPageHeaderEnabled,
    pageHeaderEnabled,
    customLogoEnabled,
    backButton,
    backButtonBehavior,
  } = guidePropertiesOptions;
  return (await axios()).patch(
    CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_PROPERTIES(themesetId),
    {
      logoId,
      logoUrl,
      landingPageHeaderEnabled,
      pageHeaderEnabled,
      customLogoEnabled,
      backButton,
      backButtonBehavior,
    },
  );
}

export async function fetchPortalInformationApi(): Promise<PortalInformation> {
  return (await axios())
    .get(API_ROUTES.PORTAL_INFORMATION)
    .then(res => res.data)
    .catch(e => {
      throw new Error(e);
    });
}

export const CONTENT_CUSTOMIZATION_ENDPOINTS = {
  FONTS: {
    PRIVATE: isDevelopment
      ? `${CONFIG.CONTENT_CUSTOMIZATION_API}/v2/fonts`
      : `/v7/content-customization/v2/fonts`,
    PUBLIC: isDevelopment
      ? `${CONFIG.CONTENT_CUSTOMIZATION_API}/v2/fonts`
      : `/v7/public-content-customization/v2/fonts`,
  },
  FONTS_NEW: {
    PRIVATE: isDevelopment
      ? `${CONFIG.CONTENT_CUSTOMIZATION_API}/v2/fontsnew`
      : `/v7/content-customization/v2/fontsnew`,
    PUBLIC: isDevelopment
      ? `${CONFIG.CONTENT_CUSTOMIZATION_API}/v2/fontsnew`
      : `/v7/public-content-customization/v2/fontsnew`,
  },
  GLOBAL_FONTS: `v7/fonts/v2/services/global?pageSize=2000`,
  FONTFACES: isDevelopment
    ? `${CONFIG.CONTENT_CUSTOMIZATION_API}/v2/fontfaces`
    : '/v7/public-content-customization/v2/fontfaces',
  THEMES: '/v7/public-content-customization/v1/themes',
  THEMESETS: isDevelopment
    ? `${CONFIG.CONTENT_CUSTOMIZATION_API}/v1/themesets/`
    : '/v7/public-content-customization/v1/themesets/',
  THEMESET_ASSIGNATION: () =>
    `${CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESETS}assignments/`,
  THEMESET_BY_ID: (themesetId: string) =>
    CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESETS + themesetId,
  DUPLICATE_THEMESET: (themesetId: string) =>
    CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_BY_ID(themesetId) + '/duplicate',
  THEMESET_TYPOGRAPHY: (themesetId: string) =>
    `${CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_BY_ID(
      themesetId,
    )}/typographies`,
  THEMESET_NAVBAR: (themesetId: string) =>
    `${CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_BY_ID(themesetId)}/navbar`,
  THEMESET_SIDEBAR: (themesetId: string) =>
    `${CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_BY_ID(themesetId)}/sidebar`,
  THEMESET_PAGE_HEADER: (themesetId: string) =>
    `${CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_BY_ID(themesetId)}/pageHeader`,
  THEMESET_PAGE_SECTION: (themesetId: string) =>
    `${CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_BY_ID(themesetId)}/pageSection`,
  THEMESET_CARD_WIDGET: (themesetId: string) =>
    `${CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_BY_ID(themesetId)}/widgets/card`,
  THEMESET_COLLECTION_WIDGET: (themesetId: string) =>
    `${CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_BY_ID(themesetId)}/widgets/collection`,
  THEMESET_BUTTON_WIDGET: (themesetId: string) =>
    `${CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_BY_ID(themesetId)}/widgets/button`,
  THEMESET_MEDIA_WIDGET: (themesetId: string) =>
    `${CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_BY_ID(themesetId)}/widgets/media`,
  THEMESET_SPACER_WIDGET: (themesetId: string) =>
    `${CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_BY_ID(themesetId)}/widgets/spacingDivider`,
  THEMESET_COLOR_PALETTE_WIDGET: (themesetId: string) =>
    `${CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_BY_ID(themesetId)}/widgets/colorPalette`,
  THEMESET_PROPERTIES: (themesetId: string) =>
    `${CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_BY_ID(themesetId)}/properties`,
  ASSIGN_THEMESET_TO_GUIDE: (themesetId: string, guideId: string) =>
    CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESET_BY_ID(themesetId) +
    '/assign/guide/' +
    guideId,
  ASSIGNED_THEMESET: (guideId: string) =>
    CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESETS + 'assign/guide/' + guideId,
  CREATE_THEMESET_COLOR: (themesetId: string, colorSetId: string) =>
    CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESETS +
    themesetId +
    '/colorSets/' +
    colorSetId +
    '/colors',
  EDIT_THEMESET_COLOR: (
    themesetId: string,
    colorSetId: string,
    colorId: string | null,
  ) =>
    CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESETS +
    themesetId +
    '/colorSets/' +
    colorSetId +
    '/colors/' +
    colorId,
  DELETE_THEMESET_COLOR: (
    themesetId: string,
    colorSetId: string,
    colorId: string,
  ) =>
    CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESETS +
    themesetId +
    '/colorSets/' +
    colorSetId +
    '/colors/' +
    colorId,
  EDIT_THEMESET_DETAILS: (themesetId: string) =>
    CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESETS + themesetId,
  DELETE_THEMESET: (themesetId: string) =>
    CONTENT_CUSTOMIZATION_ENDPOINTS.THEMESETS + themesetId,
};
